/** Utilities for working with the JavaScript (usually immutable)
 * representation of the OfferResponse data structure.
 */

import { OrderedMap, Map, Set, List } from 'immutable'
import moment from 'moment'

/** Returns a Set containing all the airport iata codes used
 * in the given offer response. Note: This relies on a hack
 * where airport URIs always end with the last 3 chars as IATA code.
 * Otherwise, we have an unpleasant situation where *some* airports
 * (in request) uses IATAs, others (in offers) uses URIs.
 */
export const getReferencedAirportIataCodes = (offerResponse = Map()) =>
    offerResponse
        .get('offerGroup', List())
        .reduce( (iatas, group ) =>
            // From requested info
            group.get('requested', List()).reduce( (iatas, req) =>
                iatas.concat( req.get('from'), req.get('to') )
                ,iatas)
                // From offers themselves
                .concat(
                    group.get('offer', List()).reduce( (iatas, offer) =>
                        offer.get('serviceDetail', List())
                            .reduce( (iatas, s) =>
                                iatas.concat(
                                    iataFromUri(s.get('from')),
                                    iataFromUri(s.get('to')))
                                ,iatas)
                        ,iatas))
                        // for connecting flights (if any)
                        .concat(
                          group.get('offer', List()).reduce( (iatas, offer) =>
                              offer.get('serviceDetail', List())
                                  .reduce( (iatas, s) =>
                                      s.has('connectingFlightDetail') ?
                                      s.get('connectingFlightDetail', List())
                                        .reduce( (iatas, cfd) =>
                                          iatas.concat(
                                            iataFromUri(cfd.get('from')),
                                            iataFromUri(cfd.get('to'))
                                            )
                                          ,iatas)
                                      :
                                      iatas.concat(
                                          iataFromUri(s.get('from')),
                                          iataFromUri(s.get('to')))
                                      ,iatas)
                              ,iatas)
                        )
            ,Set())


/** Extracts IATA from airport URI, in current naming convention. */
const iataFromUri = (uri = '') =>
    uri.substring( uri.length - 3)


/** Quick hack. In a list of offers, make the one with the given URI
 * the 'selected' one, which means it's first in the list, and has
 * a 'selected : true' propery set.
 */
export const selectOffer = ( offers = List(), offerUri ) => {
    const i = offers.findIndex( o =>
        o.get('uri') === offerUri )
    if (i < 0) {
        return offers
    } else {
        const selected = offers.get(i)
        return offers.delete(i).insert(0, selected.set('selected', true))
    }
}


/** Produce a new version of the gien OfferResponse where the offer matching
 * the offerUri is made to be 'selected' - moved to front of its group,
 * and 'selected: true' flag set.
 */
export const selectOfferInResponse = ( offerResponse = Map(), offerUri ) =>
    offerResponse.update('offerGroup', (groups = List()) =>
        groups.map( og =>
            og.update('offer', offers =>
                selectOffer(offers, offerUri))))

/** Returns a Set containing all the accommodation ID in the given offer response.
 */
export const getAccommodationIDs = (offerResponse = Map()) =>
    offerResponse.get('offerGroup', List())
      .reduce( (ids, group ) => group.get('offer', List())
        .reduce( (ids, offer) => offer.get('serviceDetail', List())
            .reduce( (ids, sd) => ids.concat( sd.get('uri') ), ids)
        ,ids)
      ,Set())


const createCombinedOfferKey = ( offer = Map() ) =>
{
  return offer.get('sourceContentProviderName') + offer.getIn(['validatingServiceProvider','industryIdentifier','identifier']) + offer.getIn(['cost','amount']);
}

/* Returns a combined set of offers for each offerGroup (getOffersResponse)\
  Combination is based on validating service provider and totalCost

  FIXME consider impact of sorting on performance
*/
export const getCombinedOffers = ( offersResponse = Map() ) =>
  new OrderedMap().set('asOf', offersResponse.get('asOf'))
    .set('company', offersResponse.get('company'))
    .set('participants', offersResponse.get('participants'))
    // 'airportGroup', offersResponse.get('airportGroup'),
    .set('combinedOfferGroup', offersResponse.get('offerGroup', List())
      .reduce ( (combinedOfferGroups, group) => {

          let oneCombinedOfferGroup = new OrderedMap().set('requested', group.get('requested'))
          let combinedOfferMap = group.get('offer').reduce( (combinedOffers, offer)=>{
            const key = createCombinedOfferKey(offer)
            if (!combinedOffers.has(key))
            {
              let combinedOffer = new OrderedMap()
                .set('sourceContentProviderName', offer.get('sourceContentProviderName'))
                .set('validUntil', offer.get('validUntil'))
                .set('validatingServiceProvider', offer.get('validatingServiceProvider'))
                .set('cost', offer.get('cost'))
                .set('serviceAlternatives', group.get('requested').reduce( (requestedList, requested)=> requestedList.push(new OrderedMap().set("requested",requested)), List()))
                .set('offerURIs', List())
              // FIXME add uri?
              combinedOffers = combinedOffers.set(key, combinedOffer)
            }
            combinedOffers = combinedOffers
              .setIn([key,'serviceAlternatives'], offer.get('serviceDetail').reduce( (serviceAlternatives, serviceDetail, index)=>
                {
                    const serviceDetailMap = serviceAlternatives.getIn([index, 'serviceDetail'], OrderedMap())
                      .set(serviceDetail.get('uri'), serviceDetail).sort((a,b)=>{
                        const aCommence = moment( a.get('commence') )
                        const bCommence = moment( b.get('commence') )
                        return aCommence.isAfter(bCommence) ? 1 : -1
                      })
                    return serviceAlternatives.setIn([index, 'serviceDetail'], serviceDetailMap)
                }, combinedOffers.getIn([key,'serviceAlternatives']))
              )
              .updateIn([key,'offerURIs'], list => list.push(offer.get('uri')))
            return combinedOffers
          }, OrderedMap() )
          return combinedOfferGroups.push(
            oneCombinedOfferGroup.set('combinedOffer', combinedOfferMap.reduce((newList, val)=>newList.push(val),List()))
          )
      }, List() ) )


const createIndexKey = ( offer ) =>
{
  return offer.getIn(["cost","amount"]) + offer.get("serviceDetail", List()).reduce((key, serviceDetail)=> key + serviceDetail.get("uri"), "")
}

/* Create a offers response index to easily get the user's chosen offer (when using combined offers)
  it is assumed that each offer is unique and the service details ordered
  however, this index takes into consideration that the user may request the same services twice (in two offer groups)
  hence, we return a list on indices per offer group instead
*/

export const createOfferIndex = (getOffersResponse = Map()) =>
{
  const a=  getOffersResponse.get('offerGroup',List()).reduce( (indices, offerGroup) => {
    return indices.push( offerGroup.get('offer', List()).reduce( (mappedOffer, offer)=> mappedOffer.set(createIndexKey(offer), offer.get("uri")), Map() ))
  }, List())
  // console.log(a.toString())
  return a
}



// WEBPACK FOOTER //
// ./src/ndc/offer/offerResponseUtils.js